import moment from "moment";
import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Input, Switch } from "rizzui";
import { PiCar } from "react-icons/pi";
import { LuCalendar } from "react-icons/lu";
import useFetch from "../../../../../hooks/useFetch";
import CustomSpinner from "../../../../../loader/CustomSpinner";
import FormGroup from "../../../../../layout/shared/form-group";
import { VehicleService } from "../../../../../services/vehicles/vehicleService";
import { VehicleApiResponse } from "../../../../../type/entities/vehicle-type";
import { vehicleStatusesSpanish } from "../../../../../utils/SelectorMapping";
import CustomSelect from "../../../../../components/forms/CustomSelect";

interface VehicleInfoProps {}

const VehicleInfo: React.FC<VehicleInfoProps> = () => {
    const { id } = useParams<{ id: string }>();
    const service = useMemo(() => new VehicleService(), []);

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getVehicleById(id);
        return response.getResponseData() as VehicleApiResponse;
    }, [id, service]));

    const statusSelectValue = useMemo(() => {
        const statusOption = vehicleStatusesSpanish.find(
            (item) => item.value === data?.status
        );
        return statusOption
            ? { value: statusOption.value, label: statusOption.label }
            : null;
    }, [data?.status]);

    if (loading) {
        return <CustomSpinner />;
    }

    if (loading || !data) {
        return (
            <div className="text-center text-red-500">
                <CustomSpinner />
            </div>
        );
    }

    return (
        <>
            <div className="@container">
                <div className="grid divide-y-2 divide-dashed divide-gray-200 gap-6">
                    <FormGroup
                        title="Información del Vehículo"
                        description='Datos principales del vehículo'
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <div className='grid grid-cols-12 md:col-span-4 gap-5'>
                            <Input
                                disabled
                                label="Placa"
                                placeholder="Número de placa"
                                prefix={<PiCar className="h-6 w-6 text-gray-500" />}
                                value={data?.plateNumber ?? ''}
                                className="col-span-12 md:col-span-6"
                            />

                            <CustomSelect
                                isDisabled
                                id={'status'}
                                label="Estado"
                                value={statusSelectValue}
                                containerClassName="col-span-12 md:col-span-6"
                            />

                            <Input
                                disabled
                                label="Marca"
                                placeholder="Marca del vehículo"
                                value={data?.brand ?? ''}
                                className="col-span-12 md:col-span-6"
                            />

                            <Input
                                disabled
                                label="Modelo"
                                placeholder="Modelo del vehículo"
                                value={data?.model ?? ''}
                                className="col-span-12 md:col-span-6"
                            />

                            <Input
                                disabled
                                label="Fecha de Matriculación"
                                placeholder="Fecha de matriculación"
                                prefix={<LuCalendar className="h-6 w-6 text-gray-500" />}
                                value={data.registrationDate ? moment(data.registrationDate?.date).format('DD/MM/YYYY') : ''}
                                className="md:col-span-12"
                            />

                            <Input
                                disabled
                                label="Fecha de Compra"
                                placeholder="Fecha de compra"
                                prefix={<LuCalendar className="h-6 w-6 text-gray-500" />}
                                value={data.purchaseDate ? moment(data.purchaseDate?.date).format('DD/MM/YYYY') : ''}
                                className="md:col-span-6"
                            />

                            <Input
                                disabled
                                label="Fecha de Venta"
                                placeholder="Fecha de venta"
                                prefix={<LuCalendar className="h-6 w-6 text-gray-500" />}
                                value={data.saleDate ? moment(data.saleDate?.date).format('DD/MM/YYYY') : ''}
                                className="md:col-span-6"
                            />

                            <Input
                                disabled
                                label="Propietario"
                                placeholder="Propietario"
                                value={data?.owner ?? ''}
                                className="col-span-12 md:col-span-12"
                            />

                            <Input
                                disabled
                                label="Potencia"
                                placeholder="Potencia"
                                value={data?.power ?? ''}
                                className="col-span-12 md:col-span-4"
                            />

                            <Input
                                disabled
                                label="Cilindrada"
                                placeholder="Cilindrada"
                                value={data?.cylinderCapacity ?? ''}
                                className="col-span-12 md:col-span-4"
                            />

                            <Input
                                disabled
                                label="Tipo de combustible"
                                placeholder="Tipo de combustible"
                                value={data?.fuelType ?? ''}
                                className="col-span-12 md:col-span-4"
                            />

                            <Switch
                                required={false}
                                id='wildCard'
                                label="Vehículo comodín"
                                className='md:col-span-12'
                                checked={data?.wildcard}
                            />

                        </div>
                    </FormGroup>

                    <FormGroup
                        title="Información del Sistema"
                        description="Datos de registro y actualización"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <div className='grid grid-cols-12 md:col-span-4 gap-5'>
                            <Input
                                disabled
                                label="Fecha de Creación"
                                placeholder="Fecha de creación"
                                prefix={<LuCalendar className="h-6 w-6 text-gray-500" />}
                                value={moment(data?.createdAt?.date).format('DD/MM/YYYY HH:mm')}
                                className="col-span-12 md:col-span-6"
                            />

                            {data?.updatedAt && (
                                <Input
                                    disabled
                                    label="Última Actualización"
                                    placeholder="Última actualización"
                                    prefix={<LuCalendar className="h-6 w-6 text-gray-500" />}
                                    value={moment(data.updatedAt?.date).format('DD/MM/YYYY HH:mm')}
                                    className="col-span-12 md:col-span-6"
                                />
                            )}
                        </div>
                    </FormGroup>
                </div>
            </div>
        </>
    );
};

export default VehicleInfo;